import React, { useState } from 'react';

import Button, { LoadingButton } from '@atlaskit/button';
import Modal, { ModalTitle, ModalTransition } from '@atlaskit/modal-dialog';
import SectionMessage from '@atlaskit/section-message';

import { ModalBody, ModalFooter } from '@qality/common-components';
import { Folder } from '@qality/common-components/src/atoms/Folder';
import { FolderSearch } from '@qality/common-components/src/molecules/FolderSearch';
import { Folder as FolderModel, TestCaseInFolder } from '@qality/common-model';
import { FolderWithParent } from '@qality/common-model/src/Folder';
import { FlagService, FolderService } from '@qality/common-service';

import { Flags } from '../TestCaseRepositoryPage.const';
import { useFoldersTree } from '../hooks/useFoldersTree';
import {
  ContainerStyled,
  FolderListContainerStyled,
  HeaderWrapperStyled,
} from './AddToFolderModal.styled';

type Props = {
  onAddToFolder: (
    folderId: FolderModel['id'],
    testCasesIds: Array<TestCaseInFolder>
  ) => void;
  closeDialog: () => void;
  testCasesIds: Array<number | string>;
  folders: Array<FolderModel>;
  getSubfolders: (folder: FolderModel) => Array<FolderModel>;
  fetchFolderById: (folderId: FolderModel['id']) => Promise<FolderModel>;
};

export default function AddToFolderModal({
  folders,
  onAddToFolder,
  closeDialog,
  testCasesIds,
  getSubfolders,
  fetchFolderById,
}: Props): JSX.Element {
  const [isLoading, setIsLoading] = useState<'confirm' | 'search' | 'none'>(
    'none'
  );
  const [chosenFolderId, setFolderId] = useState<FolderModel['id']>();
  const [showError, setShowError] = useState(false);
  const [filteredFolders, setFilteredFolders] = useState<FolderWithParent[]>(
    []
  );
  const [areFoldersFiltered, setAreFoldersFiltered] = useState(false);

  const foldersTree = useFoldersTree();

  const foldersToRender = areFoldersFiltered
    ? filteredFolders.filter((folder) => folder.canEdit)
    : folders.filter((folder) => folder.canEdit && folder.parentId === null);

  const handleFolderClick = (folder: FolderModel): void => {
    setFolderId(folder.id);
  };

  const handleExpand = async (folder: FolderModel) => {
    const hasUnloadedSubfolders =
      folder.subfoldersIds.length !== folder.totalSubfolders;
    if (hasUnloadedSubfolders) {
      await fetchFolderById(folder.id);
    }
    foldersTree.expandFolder(folder.id);
  };

  const handleCollapse = (folder: FolderModel) => {
    return foldersTree.collapseFolder(folder.id);
  };

  const handleIsExpanded = (folder: FolderModel) => {
    return foldersTree.isFolderExpanded(folder.id);
  };

  const handleIsHiglighted = (folder: FolderModel) => {
    return {
      isFolderHighlighted: folder.id === chosenFolderId,
      isWholeSubtreeHighlighted: false,
    };
  };

  const handleIsExpandable = () => {
    return !areFoldersFiltered;
  };

  const handleSelectFolder = async () => {
    if (chosenFolderId !== undefined) {
      setIsLoading('confirm');
      try {
        const response = await FolderService.addTestCasesToFolder(
          testCasesIds,
          chosenFolderId
        );
        onAddToFolder(chosenFolderId, response.data);

        FlagService.createSuccessFlag(
          'Success',
          Flags.addTestCaseToFolder.success(testCasesIds.length)
        );

        closeDialog();
      } catch {
        FlagService.createErrorFlag(
          'Error',
          Flags.addTestCaseToFolder.error(testCasesIds.length)
        );
      } finally {
        setIsLoading('none');
      }
    } else {
      setShowError(true);
    }
  };

  const handleOnSearchFolder = async (searchText: string): Promise<void> => {
    if (searchText === '') {
      setAreFoldersFiltered(false);
      return;
    }

    setIsLoading('search');

    try {
      const response = await FolderService.searchFolders(searchText);
      setFilteredFolders(response);
      setAreFoldersFiltered(true);
    } catch {
      FlagService.createErrorFlag(
        'Error',
        "We couldn't search folders. Please try again."
      );
    } finally {
      setIsLoading('none');
    }
  };

  const description = `Choose folder to place selected test cases (${testCasesIds.length}).`;

  return (
    <>
      <ModalTransition>
        <Modal
          onClose={closeDialog}
          shouldCloseOnOverlayClick={false}
          shouldCloseOnEscapePress={true}
          shouldScrollInViewport={false}
          height={480}
          width={750}
        >
          <HeaderWrapperStyled>
            <ContainerStyled>
              <ModalTitle>Select folder</ModalTitle>
              <p>{description}</p>
              <FolderSearch
                onSearch={handleOnSearchFolder}
                isLoading={isLoading === 'search'}
              />
              {showError && chosenFolderId === undefined && (
                <SectionMessage appearance='error'>
                  <p>You need to choose folder first</p>
                </SectionMessage>
              )}
            </ContainerStyled>
          </HeaderWrapperStyled>
          <ModalBody>
            <FolderListContainerStyled>
              {foldersToRender.map((folder) => (
                <Folder
                  key={folder.id}
                  folder={folder}
                  onClick={handleFolderClick}
                  isHighlighted={handleIsHiglighted}
                  isExpandable={handleIsExpandable}
                  isExpanded={handleIsExpanded}
                  onExpand={handleExpand}
                  onCollapse={handleCollapse}
                  getSubfolders={getSubfolders}
                />
              ))}
            </FolderListContainerStyled>
          </ModalBody>
          <ModalFooter>
            <Button appearance='subtle' onClick={closeDialog}>
              Cancel
            </Button>
            <LoadingButton
              isLoading={isLoading === 'confirm'}
              appearance='primary'
              onClick={handleSelectFolder}
            >
              Select folder
            </LoadingButton>
          </ModalFooter>
        </Modal>
      </ModalTransition>
    </>
  );
}
